import { useState, useEffect } from "react";
import { Copy, Download } from "lucide-react";
import { motion } from "framer-motion";
import { QRCodeCanvas } from "qrcode.react";
import { useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "../../lib/config";
import { fetchWithAuth } from "../../lib/authService";

export default function QRCard() {
  const [copied, setCopied] = useState(false);
  const [venue, setVenue] = useState(null);
  const [venues, setVenues] = useState({});
  const [selectedVenueId, setSelectedVenueId] = useState("");

  const navigate = useNavigate();
  const handleCopyLink = () => {
    if (!venue) return;
    navigator.clipboard.writeText(
      `https://user.tringbox.com/launch/venues/${selectedVenueId}`,
    );
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  // Fetch venues first (unchanged)
  useEffect(() => {
    const token = localStorage.getItem("tringAuthToken");
    if (!token) {
      navigate("/");
      return;
    }

    const fetchVenues = async () => {
      try {
        const venuesRes = await fetchWithAuth(API_ENDPOINTS.VENUE_PERFORMERS);
        const venuesData = await venuesRes.json();
        const venueMap = {};
        venuesData.venues.forEach((venue) => {
          venueMap[venue.venue.id] = venue.venue;
        });
        setVenues(venueMap);

        // Set default selectedVenueId to the first venue's id
        if (venuesData.venues.length > 0) {
          setSelectedVenueId(venuesData.venues[0].venue.id);
          setVenue(venuesData.venues[0].venue);
        }
      } catch (err) {
        setVenues({});
      }
    };

    fetchVenues();
  }, []);

  // Fetch events when selectedVenueId changes
  useEffect(() => {
    setVenue(venues[selectedVenueId]);
  }, [selectedVenueId]);

  const handleVenueChange = (e) => {
    setSelectedVenueId(e.target.value);
  };
  const handleDownloadQR = () => {
    const canvas = document.querySelector("canvas");
    if (canvas) {
      const url = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = url;
      link.download = "qr-code.png";
      link.click();
    }
  };

  useEffect(() => {
    fetchWithAuth(API_ENDPOINTS.VENUE_PERFORMERS)
      .then((r) => r.json())
      .then((data) => {
        if (data.venues && data.venues.length > 0) {
          // Use the first venue for QR/link
          setVenue(data.venues[0].venue);
        }
      })
      .catch((error) => {
        console.error("Error fetching venue:", error);
      });
  }, []);

  const notes = ["♪", "♫", "♩", "♬", "♭", "♯", "🎵", "🎶"];

  return (
    <div className="w-full flex flex-col items-center relative min-h-screen">
      {/* Musical Notes Animation */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        {Array.from({ length: 40 }).map((_, i) => (
          <motion.div
            key={`note-${i}`}
            className="absolute text-[#72288D]/20 text-3xl md:text-4xl"
            initial={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -50, 0],
              x: [0, Math.random() * 30 - 15, 0],
              opacity: [0.3, 0.7, 0.3],
              rotate: [0, Math.random() * 360, 0],
              scale: [1, 1.3, 1],
            }}
            transition={{
              duration: 4 + Math.random() * 4,
              repeat: Infinity,
              delay: Math.random() * 2,
              ease: "easeInOut",
            }}
          >
            {notes[i % notes.length]}
          </motion.div>
        ))}
      </div>

      {/* Header Strip - Hidden on mobile */}
      <div className="w-full fixed top-0 right-0 md:pl-80 z-20 hidden md:block">
        <motion.div
          className="w-full bg-white shadow-md border-b-2 border-purple-200 p-6 flex items-center justify-between gap-4"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="flex items-center space-x-3 md:mb-4 mb-2 lg:mb-0">
            {selectedVenueId && venues ? (
              <img
                className="h-10 w-10 bg-black rounded-full"
                src={venues[selectedVenueId].photoUrl}
              />
            ) : (
              <img
                className="h-10 w-10 bg-black rounded-full"
                src="https://images.tringbox.com/venues/684f36a0509a8a91342eee68/logo.png"
              />
            )}
            <div className="h-max flex flex-col gap-0 ">
              <h1 className="text-lg lg:text-xl font-bold text-[#2D0A4A] flex gap-2 items-center">
                <div className="flex items-center space-x-2 lg:space-x-4 lg:mt-0">
                  <div className="relative flex-1 lg:flex-none">
                    <select
                      value={selectedVenueId}
                      onChange={handleVenueChange}
                      className="pl-4 pr-10 py-2 w-full lg:w-80 rounded-md border border-purple-200 bg-white/50 focus:outline-none focus:ring-2 focus:ring-logo focus:border-transparent text-sm"
                    >
                      {Object.values(venues).map((venue) => (
                        <option key={venue.id} value={venue.id}>
                          {venue.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </h1>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-4xl mx-auto px-4 flex flex-col items-center relative mt-20 md:mt-56 z-10">
        {/* QR Code Section with Revolving Rectangle */}
        <div className="relative">
          {/* Revolving Rectangle Animation */}
          <motion.div
            className="absolute md:-inset-6 -inset-1 rounded-3xl border-4 border-[#72288D]/30"
            animate={{
              rotate: [0, 360],
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "linear",
            }}
          />

          {/* QR Code Container */}
          <motion.div
            className="relative bg-white md:p-8 p-3 rounded-3xl shadow-xl"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="w-64 h-64 bg-white rounded-2xl flex items-center justify-center">
              {venue && (
                <QRCodeCanvas
                  value={`https://user.tringbox.com/launch/venues/${selectedVenueId}`}
                  size={256}
                />
              )}
            </div>
          </motion.div>
        </div>

        {/* Text below QR */}
        <div className="w-full flex flex-col items-center justify-center mt-10 md:mt-16 mb-1 text-center">
          <h2 className="text-xl font-medium text-[#2D0A4A] mb-2">
            Scan to Connect
          </h2>
          <motion.h3
            className="text-3xl md:text-4xl font-bold text-[#72288D] md:mb-8 mb-2"
            initial={{ scale: 1 }}
            animate={{ scale: [1, 1.05, 1] }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            {venue?.name || "Venue"}
          </motion.h3>
        </div>

        {/* Buttons */}
        <div className="grid grid-cols-2  gap-3 w-full max-w-lg mt-6">
          <motion.button
            onClick={handleCopyLink}
            className="flex items-center justify-center gap-2 bg-white border-2 border-[#72288D] text-[#72288D] px-6 py-3 rounded-xl font-medium hover:bg-[#72288D]/5 shadow-sm"
            whileTap={{ scale: 0.98 }}
            disabled={!venue}
          >
            <Copy size={20} />
            <span>{copied ? "Copied!" : "Copy"}</span>
          </motion.button>
          <motion.button
            onClick={handleDownloadQR}
            className="flex items-center justify-center gap-2 bg-white border-2 border-[#72288D] text-[#72288D] px-6 py-3 rounded-xl font-medium hover:bg-[#72288D]/5 shadow-sm"
            whileTap={{ scale: 0.98 }}
            disabled={!venue}
          >
            <Download size={20} />
            <span>Download</span>
          </motion.button>
        </div>
      </div>
    </div>
  );
}
